<script>
  import { Icon, XMark, Sparkles, ArrowRight } from "svelte-hero-icons";
  import { createEventDispatcher, onMount } from "svelte";
  import { fly } from "svelte/transition";
  import { cubicOut } from "svelte/easing";

  const dispatch = createEventDispatcher();

  // A `KnockGuide` whose current step content follows the pre-built Card schema:
  // `headline`, `title`, `body` (markdown), optional `image` ({ url, alt }),
  // optional `primary_button` / `secondary_button` ({ text, action }),
  // `dismissible`.
  export let guide;

  $: step = guide?.getStep();
  $: content = step?.content ?? {};

  $: hasButtons = content.primary_button || content.secondary_button;

  // Rendering the card counts as the user having seen it.
  onMount(() => dispatch("seen"));

  function handleAction(action) {
    dispatch("interacted", { action });
    if (!action) return;
    // Works for absolute URLs and internal paths alike.
    if (/^https?:\/\//.test(action)) {
      window.open(action, "_blank", "noopener");
    } else {
      window.location.href = action;
    }
  }
</script>

{#if step}
  <div
    class="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm overflow-hidden rounded-2xl bg-white shadow-xl shadow-gray-900/10 ring-1 ring-gray-900/5 dark:bg-[#1f2127] dark:shadow-black/40 dark:ring-white/10"
    role="dialog"
    aria-label={content.title}
    transition:fly={{ y: 20, duration: 320, easing: cubicOut }}
  >
    <!-- Brand accent rail along the top edge, echoing the sidebar's identity. -->
    <div class="h-1 w-full bg-gradient-to-r from-brand via-indigo-500 to-sky-400"></div>

    {#if content.image?.url}
      <img src={content.image.url} alt={content.image.alt ?? ""} class="h-40 w-full object-cover" />
    {/if}

    <div class="p-5">
      {#if content.dismissible}
        <button
          type="button"
          class="absolute right-3.5 top-3.5 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-[#2a2c32] dark:hover:text-gray-200"
          on:click={() => dispatch("archived")}
          aria-label="Dismiss"
        >
          <Icon src={XMark} mini class="h-4 w-4" />
        </button>
      {/if}

      <div class="flex items-start gap-3.5">
        <!-- Icon badge mirrors the sidebar's getting-started step tiles. -->
        <span
          class="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand ring-1 ring-inset ring-brand/20 dark:bg-brand/20 dark:text-indigo-300 dark:ring-brand/30"
        >
          <Icon src={Sparkles} solid class="h-5 w-5" />
        </span>

        <div class="min-w-0 flex-1 pr-6">
          {#if content.headline}
            <span
              class="text-[11px] font-semibold uppercase tracking-wider text-brand dark:text-indigo-300"
            >
              {content.headline}
            </span>
          {/if}

          {#if content.title}
            <h3
              class="mt-0.5 text-balance text-base font-semibold leading-snug text-gray-900 dark:text-white"
            >
              {content.title}
            </h3>
          {/if}
        </div>
      </div>

      {#if content.body}
        <!-- Content is authored in the Knock dashboard, not by end users. -->
        <div
          class="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400 [&_a]:font-medium [&_a]:text-brand [&_a]:underline [&_a]:underline-offset-2 dark:[&_a]:text-indigo-300 [&_strong]:font-semibold [&_strong]:text-gray-900 dark:[&_strong]:text-gray-100"
        >
          <!-- eslint-disable-next-line svelte/no-at-html-tags -->
          {@html content.body}
        </div>
      {/if}

      {#if hasButtons}
        <div class="mt-5 flex items-center justify-end gap-2">
          {#if content.secondary_button}
            <button
              type="button"
              class="cursor-pointer rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#2a2c32]"
              on:click={() => handleAction(content.secondary_button.action)}
            >
              {content.secondary_button.text}
            </button>
          {/if}
          {#if content.primary_button}
            <button
              type="button"
              class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-brand px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#1f2127]"
              on:click={() => handleAction(content.primary_button.action)}
            >
              {content.primary_button.text}
              <Icon src={ArrowRight} mini class="h-4 w-4" />
            </button>
          {/if}
        </div>
      {:else if content.dismissible}
        <!-- No authored CTA: offer a clear, polished way to acknowledge. -->
        <div class="mt-5 flex justify-end">
          <button
            type="button"
            class="cursor-pointer rounded-lg bg-brand px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#1f2127]"
            on:click={() => dispatch("archived")}
          >
            Got it
          </button>
        </div>
      {/if}
    </div>
  </div>
{/if}
